/**
 * API types — OutpostForge frontend
 * Mirrors the backend read-Lambda response shapes.
 */

export interface OutpostSummary {
  outpostId: string;
  outpostArn: string;
  name: string;
  description?: string;
  lifeCycleStatus: string;
  availabilityZone: string;
  availabilityZoneId: string;
  supportedHardwareType: string;
  ownerId: string;
  siteId: string;
  tags: Record<string, string>;
  /** Registry mode only: false when the registration's account/role failed. */
  reachable?: boolean;
  registrationError?: string;
  region?: string;
}

/** One Outpost the tool manages (Settings registry). */
export interface OutpostRegistration {
  outpostId: string;
  accountId: string;
  region: string;
  roleArn?: string;
  name: string;
  registeredAt: string;
  registeredBy: string;
}

export interface ListRegistrationsResponse {
  registrations: OutpostRegistration[];
}

export interface RegisterOutpostRequest {
  outpostId: string;
  accountId: string;
  region: string;
  roleArn?: string;
}

export interface InstanceTypeCapacity {
  instanceType: string;
  count: number;
}

export interface AssetSummary {
  assetId: string;
  rackId: string;
  assetType: string;
  rackElevation?: number;
  hostId?: string;
  state?: string;
  instanceFamilies: string[];
  instanceTypeCapacities: InstanceTypeCapacity[];
  maxVcpus?: number;
}

export interface ListOutpostsResponse {
  outposts: OutpostSummary[];
}

export interface ListAssetsResponse {
  outpostId: string;
  assets: AssetSummary[];
  /** Present when the backend capped pagination; cursor for the next batch. */
  nextToken?: string;
}

export interface InstanceTypeItem {
  instanceType: string;
  vCPUs: number;
}

export interface ListInstanceTypesResponse {
  outpostId: string;
  configured: InstanceTypeItem[];
  supported?: InstanceTypeItem[];
}

export type TaskActionOnBlockingInstances = 'WAIT_FOR_EVACUATION' | 'FAIL_TASK';

export interface InstancesToExclude {
  instances?: string[];
  accountIds?: string[];
  services?: string[];
}

export interface StartCapacityTaskRequest {
  assetId?: string;
  orderId?: string;
  instancePools: InstanceTypeCapacity[];
  dryRun?: boolean;
  taskActionOnBlockingInstances?: TaskActionOnBlockingInstances;
  instancesToExclude?: InstancesToExclude;
}

export interface CapacityTaskFailure {
  type: string;
  reason: string;
}

export interface CapacityTaskResponse {
  capacityTaskId: string;
  outpostId: string;
  orderId: string;
  assetId?: string;
  status: string;
  dryRun: boolean;
  failed?: CapacityTaskFailure;
  requestedInstancePools: InstanceTypeCapacity[];
  creationDate?: string;
  lastModifiedDate?: string;
  completionDate?: string;
  taskActionOnBlockingInstances?: TaskActionOnBlockingInstances;
}

export interface CapacityTaskSummary {
  capacityTaskId: string;
  outpostId: string;
  orderId: string;
  assetId?: string;
  status: string;
  creationDate?: string;
  lastModifiedDate?: string;
  completionDate?: string;
}

export interface ListCapacityTasksResponse {
  tasks: CapacityTaskSummary[];
  /** Present when the backend capped pagination (single-outpost filter). */
  nextToken?: string;
  /** True when any registration was capped on the fan-out path. */
  truncated?: boolean;
}

export interface BlockingInstance {
  instanceId: string;
  accountId: string;
  awsServiceName: string;
}

export interface ListBlockingInstancesResponse {
  instances: BlockingInstance[];
  /** Present when the backend capped pagination; cursor for the next batch. */
  nextToken?: string;
}

/** Statuses that count as an active task (one per Outpost/order). */
export const ACTIVE_TASK_STATUSES = [
  'REQUESTED',
  'IN_PROGRESS',
  'WAITING_FOR_EVACUATION',
  'CANCELLATION_IN_PROGRESS',
] as const;

/** Statuses from which a task can be cancelled. */
export const CANCELLABLE_STATUSES = ['REQUESTED', 'IN_PROGRESS', 'WAITING_FOR_EVACUATION'] as const;

export interface InstanceSummary {
  instanceId: string;
  /** Name tag resolved cross-account when the lookup role is configured. */
  name?: string;
  instanceType: string;
  assetId: string;
  accountId: string;
  awsServiceName: string;
}

export interface ListInstancesResponse {
  outpostId: string;
  instances: InstanceSummary[];
  /** Present when the backend capped pagination; cursor for the next batch. */
  nextToken?: string;
}
